// src/utils/hatchPatternUtils.js
import * as THREE from 'three';

/**
 * Standart DXF Hatch Pattern Tanımları
 * Format: { angle, basePoint: [x,y], offset: [dx,dy], dashes: [...] }
 * - angle: Derece cinsinden çizgi açısı
 * - basePoint: Pattern başlangıç noktası
 * - offset: [delta-x (çizgi yönünde kayma), delta-y (dik mesafe)]
 * - dashes: Pozitif=çizgi, Negatif=boşluk, 0=nokta
 */
export const STANDARD_PATTERNS = {
  // ANSI Patterns
  ANSI31: {
    name: 'ANSI31',
    description: 'ANSI Iron, Brick, Stone masonry',
    lines: [
      { angle: 45, basePoint: [0, 0], offset: [0, 3.175], dashes: [] }
    ]
  },
  ANSI32: {
    name: 'ANSI32',
    description: 'ANSI Steel',
    lines: [
      { angle: 45, basePoint: [0, 0], offset: [0, 9.525], dashes: [] },
      { angle: 45, basePoint: [4.49, 0], offset: [0, 9.525], dashes: [] }
    ]
  },
  ANSI33: {
    name: 'ANSI33',
    description: 'ANSI Bronze, Brass, Copper',
    lines: [
      { angle: 45, basePoint: [0, 0], offset: [0, 6.35], dashes: [] },
      { angle: 45, basePoint: [4.49, 0], offset: [0, 6.35], dashes: [3.175, -1.5875] }
    ]
  },
  ANSI34: {
    name: 'ANSI34',
    description: 'ANSI Plastic, Rubber',
    lines: [
      { angle: 45, basePoint: [0, 0], offset: [0, 19.05], dashes: [] },
      { angle: 45, basePoint: [4.49, 0], offset: [0, 19.05], dashes: [] },
      { angle: 45, basePoint: [8.98, 0], offset: [0, 19.05], dashes: [] },
      { angle: 45, basePoint: [13.47, 0], offset: [0, 19.05], dashes: [] }
    ]
  },
  ANSI35: {
    name: 'ANSI35',
    description: 'ANSI Fire brick,ite material',
    lines: [
      { angle: 45, basePoint: [0, 0], offset: [0, 6.35], dashes: [] },
      { angle: 45, basePoint: [4.49, 4.49], offset: [0, 6.35], dashes: [3.175, -4.7625] }
    ]
  },
  ANSI36: {
    name: 'ANSI36',
    description: 'ANSI Marble, Slate, Glass',
    lines: [
      { angle: 45, basePoint: [0, 0], offset: [0, 3.175], dashes: [] }
    ]
  },
  ANSI37: {
    name: 'ANSI37',
    description: 'ANSI Lead, Zinc, Magnesium',
    lines: [
      { angle: 45, basePoint: [0, 0], offset: [0, 3.175], dashes: [] },
      { angle: 135, basePoint: [0, 0], offset: [0, 3.175], dashes: [] }
    ]
  },
  ANSI38: {
    name: 'ANSI38',
    description: 'ANSI Aluminum',
    lines: [
      { angle: 45, basePoint: [0, 0], offset: [0, 3.175], dashes: [] },
      { angle: 135, basePoint: [0, 0], offset: [0, 6.35], dashes: [] }
    ]
  },

  // Common Patterns
  ANGLE: {
    name: 'ANGLE',
    description: 'Angle steel',
    lines: [
      { angle: 0, basePoint: [0, 0], offset: [0, 6.985], dashes: [5.08, -1.905] },
      { angle: 90, basePoint: [0, 0], offset: [0, 6.985], dashes: [5.08, -1.905] }
    ]
  },
  BOX: {
    name: 'BOX',
    description: 'Box steel',
    lines: [
      { angle: 90, basePoint: [0, 0], offset: [0, 25.4], dashes: [] },
      { angle: 90, basePoint: [6.35, 0], offset: [0, 25.4], dashes: [6.35, -19.05] },
      { angle: 0, basePoint: [0, 0], offset: [0, 25.4], dashes: [6.35, -19.05] },
      { angle: 0, basePoint: [0, 6.35], offset: [0, 25.4], dashes: [] }
    ]
  },
  BRICK: {
    name: 'BRICK',
    description: 'Brick or masonry',
    lines: [
      { angle: 0, basePoint: [0, 0], offset: [0, 6.35], dashes: [] },
      { angle: 90, basePoint: [0, 0], offset: [0, 12.7], dashes: [6.35, -6.35] },
      { angle: 90, basePoint: [6.35, 0], offset: [0, 12.7], dashes: [-6.35, 6.35] }
    ]
  },
  CROSS: {
    name: 'CROSS',
    description: 'A series of crosses',
    lines: [
      { angle: 0, basePoint: [0, 0], offset: [6.35, 6.35], dashes: [3.175, -9.525] },
      { angle: 90, basePoint: [1.5875, -1.5875], offset: [6.35, 6.35], dashes: [3.175, -9.525] }
    ]
  },
  DASH: {
    name: 'DASH',
    description: 'Dashed lines',
    lines: [
      { angle: 0, basePoint: [0, 0], offset: [0, 3.175], dashes: [3.175, -1.5875] }
    ]
  },
  DOLMIT: {
    name: 'DOLMIT',
    description: 'Geological rock pattern',
    lines: [
      { angle: 0, basePoint: [0, 0], offset: [0, 6.35], dashes: [] },
      { angle: 45, basePoint: [0, 0], offset: [0, 17.9605], dashes: [7.1374, -10.823] }
    ]
  },
  DOTS: {
    name: 'DOTS',
    description: 'A series of dots',
    lines: [
      { angle: 0, basePoint: [0, 0], offset: [0.79375, 1.5875], dashes: [0, -1.5875] }
    ]
  },
  EARTH: {
    name: 'EARTH',
    description: 'Earth or ground',
    lines: [
      { angle: 0, basePoint: [0, 0], offset: [0, 6.35], dashes: [] },
      { angle: 0, basePoint: [0, 1.5875], offset: [0, 6.35], dashes: [6.35, -6.35] },
      { angle: 0, basePoint: [3.175, 3.175], offset: [0, 6.35], dashes: [6.35, -6.35] },
      { angle: 0, basePoint: [0, 4.7625], offset: [0, 6.35], dashes: [6.35, -6.35] }
    ]
  },
  FLEX: {
    name: 'FLEX',
    description: 'Flexible material',
    lines: [
      { angle: 45, basePoint: [0, 0], offset: [3.175, 3.175], dashes: [3.175, -3.175] }
    ]
  },
  GRASS: {
    name: 'GRASS',
    description: 'Grass area',
    lines: [
      { angle: 90, basePoint: [0, 0], offset: [17.9605, 17.9605], dashes: [7.1374, -25.0838] },
      { angle: 45, basePoint: [0, 0], offset: [17.9605, 17.9605], dashes: [7.1374, -25.0838] },
      { angle: 135, basePoint: [0, 0], offset: [17.9605, 17.9605], dashes: [7.1374, -25.0838] }
    ]
  },
  HEX: {
    name: 'HEX',
    description: 'Hexagons',
    lines: [
      { angle: 0, basePoint: [0, 0], offset: [0, 5.5033], dashes: [3.175, -3.175] },
      { angle: 120, basePoint: [0, 0], offset: [0, 5.5033], dashes: [3.175, -3.175] },
      { angle: 60, basePoint: [1.5875, 0], offset: [0, 5.5033], dashes: [3.175, -3.175] }
    ]
  },
  HONEY: {
    name: 'HONEY',
    description: 'Honeycomb pattern',
    lines: [
      { angle: 0, basePoint: [0, 0], offset: [5.5033, 9.525], dashes: [3.175, -8.255] },
      { angle: 120, basePoint: [0, 0], offset: [5.5033, 9.525], dashes: [3.175, -8.255] },
      { angle: 60, basePoint: [0, 0], offset: [5.5033, 9.525], dashes: [3.175, -8.255] }
    ]
  },
  HOUND: {
    name: 'HOUND',
    description: 'Houndstooth check',
    lines: [
      { angle: 0, basePoint: [0, 0], offset: [6.35, 6.35], dashes: [12.7, -12.7] },
      { angle: 90, basePoint: [0, 0], offset: [6.35, 6.35], dashes: [12.7, -19.05, 6.35, -12.7] }
    ]
  },
  INSUL: {
    name: 'INSUL',
    description: 'Insulation material',
    lines: [
      { angle: 0, basePoint: [0, 0], offset: [0, 9.525], dashes: [] }
    ]
  },
  LINE: {
    name: 'LINE',
    description: 'Parallel horizontal lines',
    lines: [
      { angle: 0, basePoint: [0, 0], offset: [0, 3.175], dashes: [] }
    ]
  },
  MUDST: {
    name: 'MUDST',
    description: 'Mudite geological rock pattern',
    lines: [
      { angle: 0, basePoint: [0, 0], offset: [0, 6.35], dashes: [] },
      { angle: 0, basePoint: [6.35, 3.175], offset: [0, 6.35], dashes: [6.35, -6.35] }
    ]
  },
  NET: {
    name: 'NET',
    description: 'Horizontal/vertical grid',
    lines: [
      { angle: 0, basePoint: [0, 0], offset: [0, 3.175], dashes: [] },
      { angle: 90, basePoint: [0, 0], offset: [0, 3.175], dashes: [] }
    ]
  },
  NET3: {
    name: 'NET3',
    description: 'Network pattern 0-60-120',
    lines: [
      { angle: 0, basePoint: [0, 0], offset: [0, 3.175], dashes: [] },
      { angle: 60, basePoint: [0, 0], offset: [0, 3.175], dashes: [] },
      { angle: 120, basePoint: [0, 0], offset: [0, 3.175], dashes: [] }
    ]
  },
  PLAST: {
    name: 'PLAST',
    description: 'Plastic material',
    lines: [
      { angle: 45, basePoint: [0, 0], offset: [0, 6.35], dashes: [] },
      { angle: 45, basePoint: [0, 1.5875], offset: [0, 6.35], dashes: [] }
    ]
  },
  PLASTI: {
    name: 'PLASTI',
    description: 'Plastic material (alternate)',
    lines: [
      { angle: 45, basePoint: [0, 0], offset: [0, 6.35], dashes: [] },
      { angle: 45, basePoint: [0, 1.5875], offset: [0, 6.35], dashes: [] },
      { angle: 45, basePoint: [0, 3.175], offset: [0, 6.35], dashes: [] }
    ]
  },
  SACNCR: {
    name: 'SACNCR',
    description: 'Ite concrete',
    lines: [
      { angle: 45, basePoint: [0, 0], offset: [0, 3.175], dashes: [] },
      { angle: 45, basePoint: [1.1227, 0], offset: [0, 4.4908], dashes: [3.175, -1.5875] }
    ]
  },
  SQUARE: {
    name: 'SQUARE',
    description: 'Small squares',
    lines: [
      { angle: 0, basePoint: [0, 0], offset: [0, 3.175], dashes: [3.175, -3.175] },
      { angle: 90, basePoint: [0, 0], offset: [0, 3.175], dashes: [3.175, -3.175] }
    ]
  },
  STARS: {
    name: 'STARS',
    description: 'Star pattern',
    lines: [
      { angle: 0, basePoint: [0, 0], offset: [0, 5.5033], dashes: [3.175, -3.175] },
      { angle: 60, basePoint: [0, 0], offset: [0, 5.5033], dashes: [3.175, -3.175] },
      { angle: 120, basePoint: [1.5875, 2.7495], offset: [0, 5.5033], dashes: [3.175, -3.175] }
    ]
  },
  STEEL: {
    name: 'STEEL',
    description: 'Steel material',
    lines: [
      { angle: 45, basePoint: [0, 0], offset: [0, 3.175], dashes: [] },
      { angle: 45, basePoint: [0, 0], offset: [3.175, 3.175], dashes: [12.7, -6.35] }
    ]
  },
  SWAMP: {
    name: 'SWAMP',
    description: 'Swamp area',
    lines: [
      { angle: 0, basePoint: [0, 0], offset: [0, 13.97], dashes: [25.4, -38.1] },
      { angle: 90, basePoint: [12.7, 0], offset: [50.8, 13.97], dashes: [6.35, -44.45] },
      { angle: 90, basePoint: [12.7, 6.35], offset: [50.8, 13.97], dashes: [6.35, -44.45] },
      { angle: 60, basePoint: [12.7, 0], offset: [50.8, 13.97], dashes: [7.3368, -43.4632] },
      { angle: 120, basePoint: [19.05, 0], offset: [50.8, 13.97], dashes: [7.3368, -43.4632] }
    ]
  },
  TRANS: {
    name: 'TRANS',
    description: 'Heat transfer material',
    lines: [
      { angle: 0, basePoint: [0, 0], offset: [0, 6.35], dashes: [] },
      { angle: 0, basePoint: [0, 1.5875], offset: [0, 6.35], dashes: [3.175, -1.5875] }
    ]
  },
  TRIANG: {
    name: 'TRIANG',
    description: 'Triangular pattern',
    lines: [
      { angle: 60, basePoint: [0, 0], offset: [5.5033, 9.525], dashes: [] },
      { angle: 120, basePoint: [0, 0], offset: [5.5033, 9.525], dashes: [] },
      { angle: 0, basePoint: [-2.7495, 4.7625], offset: [5.5033, 9.525], dashes: [] }
    ]
  },
  ZIGZAG: {
    name: 'ZIGZAG',
    description: 'Staircase effect',
    lines: [
      { angle: 0, basePoint: [0, 0], offset: [3.175, 3.175], dashes: [3.175, -3.175] },
      { angle: 90, basePoint: [3.175, 0], offset: [3.175, 3.175], dashes: [3.175, -3.175] }
    ]
  }
};

/**
 * Boundary noktalarından bounding box hesapla
 */
export function getBoundingBox(points) {
  let minX = Infinity, minY = Infinity;
  let maxX = -Infinity, maxY = -Infinity;

  points.forEach(p => {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  });

  return {
    min: { x: minX, y: minY },
    max: { x: maxX, y: maxY },
    width: maxX - minX,
    height: maxY - minY,
    center: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 }
  };
}

/**
 * İki doğru parçasının kesişim noktasını hesapla
 * @param {Object} p1 - İlk doğrunun başlangıç noktası {x, y}
 * @param {Object} p2 - İlk doğrunun bitiş noktası {x, y}
 * @param {Object} p3 - İkinci doğrunun başlangıç noktası {x, y}
 * @param {Object} p4 - İkinci doğrunun bitiş noktası {x, y}
 * @returns {Object|null} Kesişim noktası veya null
 */
function lineSegmentIntersection(p1, p2, p3, p4) {
  const d1x = p2.x - p1.x;
  const d1y = p2.y - p1.y;
  const d2x = p4.x - p3.x;
  const d2y = p4.y - p3.y;

  const cross = d1x * d2y - d1y * d2x;
  if (Math.abs(cross) < 1e-10) return null; // Paralel çizgiler

  const dx = p3.x - p1.x;
  const dy = p3.y - p1.y;

  const t = (dx * d2y - dy * d2x) / cross;
  const u = (dx * d1y - dy * d1x) / cross;

  // u 0-1 arasında olmalı (segment üzerinde)
  if (u < 0 || u > 1) return null;

  return {
    x: p1.x + t * d1x,
    y: p1.y + t * d1y,
    t: t // Hatch line üzerindeki pozisyon
  };
}

/**
 * Sonsuz hatch çizgisi ile boundary arasındaki kesişimleri bul
 * @param {Object} origin - Çizgi başlangıç noktası {x, y}
 * @param {Object} direction - Çizgi yön vektörü {x, y}
 * @param {Array} boundary - Boundary noktaları [{x, y}, ...]
 * @returns {Array} Sıralı kesişim noktaları
 */
export function findLinePolygonIntersections(origin, direction, boundary) {
  const intersections = [];
  const len = boundary.length;

  // Çok uzun bir çizgi oluştur
  const farDistance = 100000;
  const lineStart = {
    x: origin.x - direction.x * farDistance,
    y: origin.y - direction.y * farDistance
  };
  const lineEnd = {
    x: origin.x + direction.x * farDistance,
    y: origin.y + direction.y * farDistance
  };

  for (let i = 0; i < len; i++) {
    const p1 = boundary[i];
    const p2 = boundary[(i + 1) % len];

    const intersection = lineSegmentIntersection(lineStart, lineEnd, p1, p2);
    if (intersection) {
      intersections.push(intersection);
    }
  }

  // t değerine göre sırala
  intersections.sort((a, b) => a.t - b.t);

  // Çok yakın kesişimleri temizle (köşe noktalarından geçen çizgiler için)
  const EPSILON = 0.001; // Biraz daha büyük epsilon
  const cleanedIntersections = [];
  for (let i = 0; i < intersections.length; i++) {
    if (i === 0) {
      cleanedIntersections.push(intersections[i]);
    } else {
      const prev = cleanedIntersections[cleanedIntersections.length - 1];
      const curr = intersections[i];
      // Eğer iki kesişim çok yakınsa, atla (duplicate)
      const dist = Math.sqrt(
        Math.pow(curr.x - prev.x, 2) + Math.pow(curr.y - prev.y, 2)
      );
      if (dist > EPSILON) {
        cleanedIntersections.push(curr);
      }
    }
  }

  // Tek sayıda kesişim varsa, çift yapmak için son kesişimi at
  // (Bu, köşeden geçen çizgilerde oluşabilir)
  if (cleanedIntersections.length % 2 !== 0 && cleanedIntersections.length > 0) {
    cleanedIntersections.pop();
  }

  return cleanedIntersections;
}

/**
 * Dash pattern uygulayarak line segmentleri oluştur
 * @param {Object} start - Başlangıç noktası {x, y}
 * @param {Object} end - Bitiş noktası {x, y}
 * @param {Array} dashes - Dash pattern [pozitif=çizgi, negatif=boşluk, 0=nokta]
 * @param {number} scale - Pattern ölçeği
 * @param {number} phaseOffset - Dash pattern başlangıç offset'i (çizgi yönünde mesafe)
 * @returns {Array} Line segment noktaları
 */
export function applyDashPattern(start, end, dashes, scale = 1, phaseOffset = 0) {
  const segments = [];

  if (!dashes || dashes.length === 0) {
    // Solid line
    segments.push({ start: { ...start }, end: { ...end } });
    return segments;
  }

  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const totalLength = Math.sqrt(dx * dx + dy * dy);

  if (totalLength < 1e-10) return segments;

  const dirX = dx / totalLength;
  const dirY = dy / totalLength;

  // Total dash cycle length
  let cycleLength = 0;
  dashes.forEach(d => cycleLength += Math.abs(d) * scale);

  if (cycleLength < 1e-10) {
    segments.push({ start: { ...start }, end: { ...end } });
    return segments;
  }

  // Phase offset'i cycle içinde normalize et
  let normalizedOffset = phaseOffset % cycleLength;
  if (normalizedOffset < 0) normalizedOffset += cycleLength;

  // Başlangıç dash index ve pozisyonunu bul
  let dashIndex = 0;
  let accumulatedLen = 0;

  while (accumulatedLen + Math.abs(dashes[dashIndex]) * scale <= normalizedOffset) {
    accumulatedLen += Math.abs(dashes[dashIndex]) * scale;
    dashIndex = (dashIndex + 1) % dashes.length;
    if (dashIndex === 0 && accumulatedLen > 0) break; // Tam bir cycle döndük
  }

  // İlk dash içindeki kalan mesafe
  let remainingInCurrentDash = (accumulatedLen + Math.abs(dashes[dashIndex]) * scale) - normalizedOffset;
  let currentPos = 0;

  // İlk kısmi dash'i çiz
  const firstDashLen = dashes[dashIndex] * scale;
  if (firstDashLen > 0 && remainingInCurrentDash > 0) {
    const dashEnd = Math.min(remainingInCurrentDash, totalLength);
    segments.push({
      start: { x: start.x, y: start.y },
      end: { x: start.x + dirX * dashEnd, y: start.y + dirY * dashEnd }
    });
  }

  currentPos = remainingInCurrentDash;
  dashIndex = (dashIndex + 1) % dashes.length;

  // Kalan dash'leri çiz
  while (currentPos < totalLength) {
    const dashLen = dashes[dashIndex] * scale;
    const absDashLen = Math.abs(dashLen);

    if (dashLen > 0) {
      // Draw dash
      const dashStart = {
        x: start.x + dirX * currentPos,
        y: start.y + dirY * currentPos
      };
      const dashEnd = {
        x: start.x + dirX * Math.min(currentPos + absDashLen, totalLength),
        y: start.y + dirY * Math.min(currentPos + absDashLen, totalLength)
      };
      if (currentPos < totalLength) {
        segments.push({ start: dashStart, end: dashEnd });
      }
    } else if (dashLen === 0) {
      // Draw dot (very small line)
      if (currentPos < totalLength) {
        const dotPos = {
          x: start.x + dirX * currentPos,
          y: start.y + dirY * currentPos
        };
        segments.push({ start: dotPos, end: { x: dotPos.x + 0.01, y: dotPos.y + 0.01 } });
      }
    }
    // Negative = gap, do nothing

    currentPos += absDashLen;
    dashIndex = (dashIndex + 1) % dashes.length;
  }

  return segments;
}

/**
 * Shape'den boundary noktalarını çıkar
 * @param {THREE.Shape} shape - THREE.js Shape objesi
 * @param {number} divisions - Eğri bölüm sayısı
 * @returns {Array} Boundary noktaları [{x, y}, ...]
 */
export function extractBoundaryFromShape(shape, divisions = 64) {
  const points = shape.getPoints(divisions);
  return points.map(p => ({ x: p.x, y: p.y }));
}

/**
 * Pattern tanımından hatch çizgilerini oluştur
 * @param {Array} boundary - Boundary noktaları [{x, y}, ...]
 * @param {Object} patternDef - Pattern tanımı
 * @param {number} scale - Pattern ölçeği
 * @param {number} angle - Ek rotasyon açısı (derece)
 * @returns {Array} Line segment listesi [{start, end}, ...]
 */
export function generateHatchLines(boundary, patternDef, scale = 1, angle = 0) {
  const allSegments = [];

  if (!boundary || boundary.length < 3) {
    console.warn('generateHatchLines: Invalid boundary');
    return allSegments;
  }

  const bounds = getBoundingBox(boundary);
  const diagonal = Math.sqrt(bounds.width * bounds.width + bounds.height * bounds.height);

  // Her pattern line için
  patternDef.lines.forEach(lineDef => {
    const totalAngle = (lineDef.angle + angle) * Math.PI / 180;
    const direction = {
      x: Math.cos(totalAngle),
      y: Math.sin(totalAngle)
    };

    // Perpendicular direction (offset yönü - çizgiye dik)
    const perpDir = {
      x: -Math.sin(totalAngle),
      y: Math.cos(totalAngle)
    };

    // Base point (pattern origin) - ölçeklenmiş
    // DXF'den gelen basePoint global koordinat, standart pattern'ler için relatif
    const basePoint = {
      x: (lineDef.basePoint?.[0] || 0) * scale,
      y: (lineDef.basePoint?.[1] || 0) * scale
    };

    // Offset değerleri
    const deltaX = (lineDef.offset?.[0] || 0) * scale; // Çizgi yönünde kayma (stagger)
    const deltaY = (lineDef.offset?.[1] || 1) * scale; // Dik mesafe (spacing)

    // Dik mesafe (perpendicular spacing)
    const perpSpacing = Math.abs(deltaY);
    if (perpSpacing < 0.001) return; // Çok küçük spacing, atla

    // Pattern origin: basePoint büyük değerlerse (>100) global koordinat olarak kullan
    // Küçük değerlerse (standart pattern) bounds.center'a göre hesapla (merkezden geçen çizgi için)
    const isGlobalBasePoint = Math.abs(lineDef.basePoint?.[0] || 0) > 100 ||
                               Math.abs(lineDef.basePoint?.[1] || 0) > 100;

    const patternOrigin = isGlobalBasePoint
      ? { x: basePoint.x, y: basePoint.y }  // DXF global koordinat
      : { x: bounds.center.x + basePoint.x, y: bounds.center.y + basePoint.y }; // Merkez + offset

    // Kaç çizgi gerekiyor?
    // Güvenlik limiti: devasa alanlarda binlerce çizgi üretimi yavaşlatır.
    const MAX_NUM_LINES = 6000;
    const numLines = Math.min(Math.ceil(diagonal / perpSpacing) * 2 + 4, MAX_NUM_LINES);

    // Dash cycle length hesapla (phase offset için)
    let dashCycleLength = 0;
    if (lineDef.dashes && lineDef.dashes.length > 0) {
      lineDef.dashes.forEach(d => dashCycleLength += Math.abs(d) * scale);
    }

 

    // Pattern origin'den başlayarak çizgiler oluştur
    for (let i = -numLines; i <= numLines; i++) {
      // Çizgi origin'i hesapla:
      // - perpDir yönünde i * perpSpacing kadar ilerle (paralel çizgiler)
      // - direction yönünde i * deltaX kadar kayma ekle (stagger effect)
      const lineOrigin = {
        x: patternOrigin.x + perpDir.x * perpSpacing * i + direction.x * deltaX * i,
        y: patternOrigin.y + perpDir.y * perpSpacing * i + direction.y * deltaX * i
      };

      // Boundary ile kesişimleri bul
      const intersections = findLinePolygonIntersections(lineOrigin, direction, boundary);

  

      // Çift sayıda kesişim olmalı (giriş-çıkış)
      for (let j = 0; j < intersections.length - 1; j += 2) {
        const start = intersections[j];
        const end = intersections[j + 1];

        // Phase offset hesapla: İki bileşenden oluşur:
        // 1. Kesişim noktasının pattern GLOBAL origin'den çizgi yönündeki mesafesi
        // 2. Stagger offset (deltaX * i) - tuğla gibi şaşırtmalı desenler için
        let phaseOffset = 0;
        if (dashCycleLength > 0) {
          // Start noktasının patternOrigin'den (global) çizgi yönündeki mesafesi
          const toStart = {
            x: start.x - patternOrigin.x,
            y: start.y - patternOrigin.y
          };
          // Çizgi yönündeki projeksiyon (dot product)
          const basePhase = toStart.x * direction.x + toStart.y * direction.y;

          // Stagger effect: Her çizgi deltaX * i kadar phase kayması
          // Bu, tuğla deseninde bir satır çizgi, bir satır boşluk etkisi yaratır
          const staggerPhase = deltaX * i;

          phaseOffset = basePhase + staggerPhase;
        }

        // Dash pattern uygula (phase offset ile)
        const segments = applyDashPattern(start, end, lineDef.dashes, scale, phaseOffset);
        allSegments.push(...segments);
      }
    }
  });

  return allSegments;
}

/**
 * Hatch çizgilerinden Three.js LineSegments geometry oluştur
 * @param {Array} segments - Line segment listesi [{start, end}, ...]
 * @param {number} color - Renk değeri
 * @param {number} zOffset - Z pozisyon offset'i
 * @returns {THREE.LineSegments} Three.js LineSegments objesi
 */
export function createHatchLineGeometry(segments, color = 0x000000, zOffset = 0.1) {
  if (!segments || segments.length === 0) {
    return null;
  }

  const positions = [];

  segments.forEach(seg => {
    positions.push(seg.start.x, seg.start.y, zOffset);
    positions.push(seg.end.x, seg.end.y, zOffset);
  });

 

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

  const material = new THREE.LineBasicMaterial({
    color: color,
    linewidth: 1
  });

  const lineSegments = new THREE.LineSegments(geometry, material);
  // Pattern çizgileri görsel amaçlı; raycasting'den hariç tut.
  // Bu olmadan 59K+ segment içeren hatch geometry'si mouse hover'da her frame
  // binlerce kesişim testi yapar → sahne yavaşlar.
  lineSegments.raycast = () => {};
  return lineSegments;
}

/**
 * DXF pattern verisinden pattern tanımı oluştur
 * @param {Object} dxfPattern - DXF'den parse edilen pattern verisi
 * @returns {Object} Pattern tanımı
 */
export function convertDxfPatternToDefinition(dxfPattern) {
  if (!dxfPattern || !dxfPattern.patternLine) {
    return null;
  }

  const lines = dxfPattern.patternLine.map(line => ({
    angle: line.lineAngle || 0,
    basePoint: [line.LineBasePointX || 0, line.LineBasePointY || 0],
    offset: [line.LineOffsetX || 0, line.LineOffsetY || 1],
    dashes: line.dashes || []
  }));

  return { lines };
}

/**
 * Pattern adından standart pattern'i al
 * @param {string} name - Pattern adı
 * @returns {Object|null} Pattern tanımı veya null
 */
export function getStandardPattern(name) {
  if (!name) return null;

  const upperName = name.toUpperCase().trim();

  // SOLID pattern için null döndür (solid fill kullanılacak)
  if (upperName === 'SOLID') return null;

  return STANDARD_PATTERNS[upperName] || null;
}

/**
 * Tüm standart pattern isimlerini listele
 * @returns {Array} Pattern isimleri
 */
export function listStandardPatterns() {
  return Object.keys(STANDARD_PATTERNS);
}